// ShortsDetailJsonLd.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import serialize from 'serialize-javascript';
import { useTranslation } from 'react-i18next';
import { formatISO } from 'date-fns';

import {
  getCoporation,
  getPerson,
  getInteractionCounter,
  getShortSchemaObject,
} from '../resource/SchemaObject.js';
import {
  getAssetManifest,
  MediaAssetFormat,
  MediaPreset,
} from '../resource/getMediaAsset.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

export const ShortsDetailJsonLd = ({
  messageId,
  senderId,
  senderUsername,
  title,
  captionText,
  ogMessageThumbnail,
  firstAssetId,
  sourceId,
  postedAtUnix,
  unlockPrice,
  viewCount,
  likeCount,
  firstAssetDuration,
  hashtagContentList,
}) => {
  const { t } = useTranslation();
  const senderSchemaObject = useMemo(() => {
    if (!senderId || !senderUsername) {
      return;
    }
    return getPerson({
      id: senderId,
      username: senderUsername,
    });
  }, [senderId, senderUsername]);
  const shortSchemaObject = useMemo(() => {
    if (!messageId || !senderId || !senderUsername || !firstAssetId) {
      return;
    }
    return getShortSchemaObject({
      id: messageId,
      title,
      description: captionText,
      durationSecs: firstAssetDuration,
      author: {
        '@id': `https://swag.live/user/${senderId}#person`,
      },
      username: senderUsername,
      postedAtUnix,
      isFree: unlockPrice === 0,
      interactionStatistic: [
        { type: 'ViewAction', count: viewCount },
        { type: 'LikeAction', count: likeCount },
      ].map(getInteractionCounter),
      encodings: [
        { '@id': `https://swag.live/shorts/${messageId}#video-dash` },
      ],
      associatedMedia: {
        '@id': `https://swag.live/post/${sourceId}#preview`,
      },
      thumbnailUrl: ogMessageThumbnail,
      contentUrl: getAssetManifest({
        id: messageId,
        assetId: firstAssetId,
        format: MediaAssetFormat.M3U8,
        preset: MediaPreset.SD,
      })?.href,
      hashtags: hashtagContentList.map(content => `#${content}`),
    });
  }, [
    captionText,
    firstAssetDuration,
    firstAssetId,
    hashtagContentList,
    likeCount,
    messageId,
    ogMessageThumbnail,
    postedAtUnix,
    senderId,
    senderUsername,
    sourceId,
    title,
    unlockPrice,
    viewCount,
  ]);
  const hashtagSchemaObjects = useMemo(() => {
    return hashtagContentList.map(hashtag => ({
      '@type': 'VideoGallery',
      '@id': `https://swag.live/hashtag/${hashtag}/flix#webpage`,
      url: `https://swag.live/hashtag/${hashtag}/flix`,
      name: `#${hashtag}`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': 'https://swag.live',
              '@type': 'WebPage',
              name: t('seo-home-title', {
                ns: TranslationNamespace.SEO,
              }),
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': `https://swag.live/hashtag/${hashtag}/flix#feed`,
              '@type': 'WebPage',
              name: `#${hashtag} ${t('seo_flix_hashtag_title', {
                ns: TranslationNamespace.SEO,
              })} - ${t('seo_video_title', {
                ns: TranslationNamespace.SEO,
              })}`,
            },
          },
        ],
      },
      mainEntity: {
        '@id': `https://swag.live/hashtag/${hashtag}/flix#feed`,
      },
    }));
  }, [hashtagContentList, t]);
  const jsonLd = useMemo(() => {
    if (!senderSchemaObject || !shortSchemaObject) {
      return null;
    }
    const translationParameters = {
      cp_name: senderUsername,
      message_title: title,
      message_description: captionText,
      ns: TranslationNamespace.SEO,
    };
    return serialize(
      {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'BlogPosting',
            headline: t('seo_cp_shorts_title', translationParameters),
            articleBody: t('seo_cp_shorts_description', translationParameters),
            sharedContent: {
              '@id': shortSchemaObject['@id'],
            },
            image: [ogMessageThumbnail],
            datePublished: postedAtUnix
              ? formatISO(postedAtUnix * 1000)
              : undefined,
            author: {
              '@id': senderSchemaObject['@id'],
            },
            publisher: getCoporation(),
          },
          senderSchemaObject,
          shortSchemaObject,
          ...hashtagSchemaObjects,
        ],
      },
      { isJSON: true }
    );
  }, [
    captionText,
    hashtagSchemaObjects,
    ogMessageThumbnail,
    postedAtUnix,
    senderSchemaObject,
    senderUsername,
    shortSchemaObject,
    t,
    title,
  ]);
  return (
    <Helmet>
      <script type="application/ld+json" helmetkey="jsonld">
        {jsonLd}
      </script>
    </Helmet>
  );
};

ShortsDetailJsonLd.propTypes = {
  messageId: PropTypes.string,
  senderId: PropTypes.string,
  senderUsername: PropTypes.string,
  title: PropTypes.string,
  captionText: PropTypes.string,
  ogMessageThumbnail: PropTypes.string,
  firstAssetId: PropTypes.string,
  sourceId: PropTypes.string,
  postedAtUnix: PropTypes.number,
  unlockPrice: PropTypes.number,
  viewCount: PropTypes.number,
  likeCount: PropTypes.number,
  firstAssetDuration: PropTypes.number,
  hashtagContentList: PropTypes.array,
};

ShortsDetailJsonLd.defaultProps = {
  messageId: null,
  senderId: null,
  senderUsername: '',
  title: '',
  captionText: '',
  ogMessageThumbnail: '',
  firstAssetId: null,
  sourceId: null,
  postedAtUnix: null,
  unlockPrice: null,
  viewCount: 0,
  likeCount: 0,
  firstAssetDuration: 0,
  hashtagContentList: [],
};

export default ShortsDetailJsonLd;
